/**
 * Settings Helpers - Shared utilities for settings pages
 * Provides common functions and constants for AJAX field updates
 *
 * @author InterPals Development Team
 * @version 1.0.0
 */

(function() {
    'use strict';

    // Constants
    var FEEDBACK_DURATION = {
        SUCCESS: 1200,
        ERROR: 2500
    };

    var CSS_CLASSES = {
        HIDDEN: 'hidden',
        OPACITY_70: 'opacity-70',
        POINTER_EVENTS_NONE: 'pointer-events-none',
        SUCCESS_FEEDBACK: 'transition-all duration-300 bg-green-50',
        ERROR_FEEDBACK: 'transition-all duration-300 bg-red-50'
    };

    // Unified endpoint for all settings field updates
    var FIELD_UPDATE_URL = '/app/settings/field/update';

    var SELECTORS = {
        CSRF_TOKEN_META: 'meta[name="csrf_token"]',
        CSRF_TOKEN_INPUT: 'input[name="csrf_token"]'
    };

    var HTTP_HEADERS = {
        X_REQUESTED_WITH: 'X-Requested-With',
        XML_HTTP_REQUEST: 'XMLHttpRequest'
    };

    /**
     * Get CSRF token from meta tag or hidden input
     * @returns {string|null} CSRF token value or null if not found
     */
    function getCsrfToken() {
        var csrfTokenMeta = document.querySelector(SELECTORS.CSRF_TOKEN_META);
        var csrfTokenInput = document.querySelector(SELECTORS.CSRF_TOKEN_INPUT);

        if (csrfTokenMeta) {
            return csrfTokenMeta.getAttribute('content');
        } else if (csrfTokenInput) {
            return csrfTokenInput.value;
        }

        return null;
    }

    /**
     * Apply visual feedback to an element
     * @param {HTMLElement} element - The element
     * @param {string} type - 'success' or 'error'
     * @param {number} duration - Duration in milliseconds (optional, uses default)
     */
    function applyVisualFeedback(element, type, duration) {
        if (!element) return;

        var feedbackClasses = {
            success: CSS_CLASSES.SUCCESS_FEEDBACK,
            error: CSS_CLASSES.ERROR_FEEDBACK
        };

        var className = feedbackClasses[type];
        if (!className) return;

        var defaultDuration = type === 'success' ? FEEDBACK_DURATION.SUCCESS : FEEDBACK_DURATION.ERROR;
        var actualDuration = duration !== undefined ? duration : defaultDuration;

        // Remove any existing feedback classes and opacity
        element.classList.remove('bg-green-50', 'bg-red-50', CSS_CLASSES.OPACITY_70);

        // Force a reflow to ensure the removal takes effect
        void element.offsetWidth;

        // Apply feedback classes
        var feedbackClassList = className.split(' ');
        feedbackClassList.forEach(function(cls) {
            element.classList.add(cls);
        });

        // Remove feedback classes after duration
        setTimeout(function() {
            feedbackClassList.forEach(function(cls) {
                element.classList.remove(cls);
            });
            // Also ensure opacity is removed
            element.classList.remove(CSS_CLASSES.OPACITY_70);
        }, actualDuration);
    }

    /**
     * Cleanup function to restore field state
     * @param {HTMLElement} field - The field element
     * @param {HTMLElement} container - The container element
     * @param {HTMLElement} spinnerElement - The spinner element (optional)
     */
    function cleanupFieldState(field, container, spinnerElement) {
        // Re-enable field
        if (field) {
            field.disabled = false;
        }

        // Hide spinner
        if (spinnerElement) {
            spinnerElement.classList.add(CSS_CLASSES.HIDDEN);
        }

        // Restore visual state
        if (container) {
            container.classList.remove(CSS_CLASSES.OPACITY_70, CSS_CLASSES.POINTER_EVENTS_NONE);
        }
    }

    /**
     * Send a field update request to the server
     * @param {Object} options - Request options
     * @param {string} options.fieldName - Field name
     * @param {string} options.fieldValue - Field value
     * @param {HTMLElement} options.container - Container element for visual feedback
     * @param {HTMLElement} options.field - The field element to disable during save (optional)
     * @param {HTMLElement} options.spinner - Spinner element to show (optional)
     * @param {Function} options.onSuccess - Success callback (optional)
     * @param {Function} options.onError - Error callback (optional)
     * @param {string} options.url - Custom URL (optional, defaults to FIELD_UPDATE_URL)
     * @param {number} options.timeout - Request timeout in ms (optional, defaults to 10000)
     */
    function sendFieldUpdate(options) {
        var fieldName = options.fieldName;
        var fieldValue = options.fieldValue;
        var container = options.container;
        var field = options.field;
        var spinner = options.spinner;
        var onSuccess = options.onSuccess;
        var onError = options.onError;
        var url = options.url || FIELD_UPDATE_URL;
        var timeout = options.timeout || 10000;

        // Add visual loading state
        if (container) {
            container.classList.add(CSS_CLASSES.OPACITY_70);
        }
        if (field) {
            field.disabled = true;
        }
        if (spinner) {
            spinner.classList.remove(CSS_CLASSES.HIDDEN);
        }

        // Set timeout for cleanup
        var timeoutId = setTimeout(function() {
            cleanupFieldState(field, container, spinner);
        }, timeout);

        // Prepare form data
        var formData = new FormData();
        formData.append('field', fieldName);
        formData.append('value', fieldValue);

        // Add CSRF token
        var tokenValue = getCsrfToken();
        if (tokenValue) {
            formData.append('csrf_token', tokenValue);
        }

        // Make AJAX request
        fetch(url, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(function(response) {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(function(data) {
            clearTimeout(timeoutId);

            // Always remove loading state first
            cleanupFieldState(field, container, spinner);

            if (data.success) {
                // Show success feedback
                applyVisualFeedback(container, 'success', FEEDBACK_DURATION.SUCCESS);
                if (onSuccess) {
                    onSuccess(data);
                }
            } else {
                throw new Error(data.error_msg || 'Unknown error occurred');
            }
        })
        .catch(function(error) {
            console.error('Settings field update failed:', error);
            clearTimeout(timeoutId);

            // Always remove loading state first
            cleanupFieldState(field, container, spinner);

            // Show error feedback
            applyVisualFeedback(container, 'error', FEEDBACK_DURATION.ERROR);
            if (onError) {
                onError(error);
            }
        });
    }

    // Export to window for use by other settings modules
    window.SettingsHelpers = {
        FEEDBACK_DURATION: FEEDBACK_DURATION,
        CSS_CLASSES: CSS_CLASSES,
        FIELD_UPDATE_URL: FIELD_UPDATE_URL,
        SELECTORS: SELECTORS,
        HTTP_HEADERS: HTTP_HEADERS,
        getCsrfToken: getCsrfToken,
        applyVisualFeedback: applyVisualFeedback,
        cleanupFieldState: cleanupFieldState,
        sendFieldUpdate: sendFieldUpdate
    };

})();
